import { Icon } from "@iconify/react/dist/iconify.js";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { currentUserQueryOptions } from "$/modules/auth/lib/queries";
import { BrandLogo } from "$/modules/shared/ui/brand-logo";
import { Button } from "$/modules/shared/ui/button";
import { Card } from "$/modules/shared/ui/card";

export const Route = createFileRoute("/$locale/")({
	component: Home,
	beforeLoad: async ({ params, context }) => {
		const user = await context.queryClient.fetchQuery(
			currentUserQueryOptions(),
		);

		if (user) {
			throw redirect({
				to: "/$locale/$userId/projects",
				params: { locale: params.locale, userId: user.$id },
			});
		}
	},
});

const NAV_ITEMS = ["features", "workflow", "stories", "pricing"] as const;

const FEATURE_ITEMS = [
	{ key: "quizModes", icon: "layers-3" },
	{ key: "questionBuilder", icon: "list-checks" },
	{ key: "media", icon: "image" },
	{ key: "ai", icon: "sparkles" },
	{ key: "sharing", icon: "qr-code" },
	{ key: "liveRounds", icon: "radio-tower" },
	{ key: "exams", icon: "graduation-cap" },
	{ key: "collaboration", icon: "users-round" },
	{ key: "data", icon: "file-output" },
] as const;

const WORKFLOW_STEPS = ["one", "two", "three"] as const;

function Home() {
	const t = useTranslations("landingPage");
	const { locale } = Route.useParams();

	return (
		<div className="min-h-screen bg-background text-foreground">
			{/* Nav */}
			<header className="sticky top-0 z-40 border-b border-default-100 bg-background/80 backdrop-blur-lg">
				<nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
					<Link
						to="/$locale"
						params={{ locale }}
						className="flex items-center gap-2 font-display text-xl font-bold"
					>
						<BrandLogo />
						{t("brand")}
					</Link>
					<div className="hidden items-center gap-8 md:flex">
						{NAV_ITEMS.map((item) => (
							<a
								key={item}
								href={`#${item}`}
								className="text-sm font-medium text-foreground-500 transition-colors hover:text-foreground"
							>
								{t(`nav.${item}`)}
							</a>
						))}
					</div>
					<Button asChild size="sm" variant="default">
						<Link to="/$locale/signup" params={{ locale }}>
							{t("hero.primaryCta")}
						</Link>
					</Button>
				</nav>
			</header>

			{/* Hero */}
			<section className="relative overflow-hidden">
				<div className="pointer-events-none absolute inset-0 -z-10">
					<div className="absolute -top-32 right-[-10%] size-[28rem] rounded-full bg-primary/20 blur-3xl" />
					<div className="absolute bottom-[-20%] left-[-10%] size-[24rem] rounded-full bg-secondary/20 blur-3xl" />
				</div>

				<div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-16 pt-12 sm:px-6 sm:pt-16 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:pb-24 lg:pt-20">
					<div className="animate-[landing-rise_0.6s_ease-out]">
						<span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
							<Icon icon="lucide:sparkles" className="size-4" />
							{t("hero.kicker")}
						</span>
						<h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
							{t("hero.title")}
							<span className="block text-primary">
								{t("hero.titleAccent")}
							</span>
						</h1>
						<p className="mt-6 max-w-xl text-lg text-foreground-500">
							{t("hero.description")}
						</p>
						<div className="mt-8 flex flex-col gap-3 sm:flex-row">
							<Button
								asChild
								size="lg"
								className="w-full font-semibold sm:w-auto"
								variant="default"
							>
								<Link to="/$locale/signup" params={{ locale }}>
									{t("hero.primaryCta")}
									{<Icon icon="lucide:arrow-right" className="size-5" />}
								</Link>
							</Button>
							<Button
								asChild
								size="lg"
								className="w-full sm:w-auto"
								variant="outline"
							>
								<Link to="/$locale/signin" params={{ locale }}>
									{t("hero.secondaryCta")}
								</Link>
							</Button>
						</div>
						<div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-foreground-500">
							<span className="flex items-center gap-2">
								<Icon
									icon="lucide:shield-check"
									className="size-4 text-success"
								/>
								{t("hero.note")}
							</span>
							<span className="flex items-center gap-2">
								<Icon icon="lucide:zap" className="size-4 text-secondary" />
								{t("hero.noteTwo")}
							</span>
						</div>
					</div>

					{/* Signature: live quiz preview card */}
					<div className="relative animate-[landing-fade_0.8s_ease-out]">
						<Card className="rotate-1 border border-default-200/80 p-5 transition-transform hover:rotate-0 sm:p-6">
							<div className="flex items-center justify-between">
								<span className="text-xs font-semibold uppercase tracking-wider text-primary">
									{t("preview.kicker")}
								</span>
								<span className="rounded-full bg-warning/15 px-2 py-0.5 text-xs font-medium text-warning-700">
									{t("preview.badge")}
								</span>
							</div>
							<h3 className="mt-2 font-display text-xl font-bold">
								{t("preview.title")}
							</h3>
							<p className="mt-4 font-medium">{t("preview.question")}</p>
							<div className="mt-3 space-y-2">
								{["optionOne", "optionTwo", "optionThree"].map((opt, index) => (
									<div
										key={opt}
										className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-sm ${
											index === 0
												? "border-primary bg-primary/5 font-medium"
												: "border-default-200"
										}`}
									>
										<span
											className={`flex size-6 items-center justify-center rounded-full text-xs font-bold ${
												index === 0
													? "bg-primary text-primary-foreground"
													: "bg-default-100 text-foreground-500"
											}`}
										>
											{String.fromCharCode(65 + index)}
										</span>
										{t(`preview.${opt}`)}
									</div>
								))}
							</div>
							<div className="mt-5 grid grid-cols-2 gap-3">
								<div className="rounded-xl bg-default-100 p-3">
									<p className="text-xs text-foreground-500">
										{t("preview.metricOneLabel")}
									</p>
									<p className="font-display text-lg font-bold">
										{t("preview.metricOneValue")}
									</p>
								</div>
								<div className="rounded-xl bg-default-100 p-3">
									<p className="text-xs text-foreground-500">
										{t("preview.metricTwoLabel")}
									</p>
									<p className="font-display text-lg font-bold">
										{t("preview.metricTwoValue")}
									</p>
								</div>
							</div>
							<Button className="mt-4 w-full" variant="soft" type="button">
								{<Icon icon="lucide:link" className="size-4" />}
								{t("preview.cta")}
							</Button>
						</Card>
					</div>
				</div>
			</section>

			{/* Stats */}
			<section className="border-y border-default-100 bg-content1/50">
				<div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-8 sm:grid-cols-3 sm:px-6 lg:px-8">
					{(
						[
							["teamsValue", "teamsLabel"],
							["questionsValue", "questionsLabel"],
							["minutesValue", "minutesLabel"],
						] as const
					).map(([value, label]) => (
						<div key={label} className="text-center sm:text-left">
							<p className="font-display text-3xl font-bold text-primary">
								{t(`stats.${value}`)}
							</p>
							<p className="text-sm text-foreground-500">
								{t(`stats.${label}`)}
							</p>
						</div>
					))}
				</div>
			</section>

			{/* Features */}
			<section
				id="features"
				className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
			>
				<div className="mx-auto max-w-2xl text-center">
					<span className="text-sm font-semibold uppercase tracking-wider text-primary">
						{t("featuresSection.kicker")}
					</span>
					<h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
						{t("featuresSection.title")}
					</h2>
					<p className="mt-4 text-foreground-500">
						{t("featuresSection.description")}
					</p>
				</div>
				<div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
					{FEATURE_ITEMS.map((feature) => (
						<Card
							key={feature.key}
							className="border border-default-200/80 p-6 transition-colors hover:border-primary/30 hover:bg-default-50/60"
						>
							<div className="mb-4 flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
								<Icon icon={`lucide:${feature.icon}`} className="size-6" />
							</div>
							<h3 className="text-lg font-semibold">
								{t(`featuresSection.items.${feature.key}.title`)}
							</h3>
							<p className="mt-2 text-sm text-foreground-500">
								{t(`featuresSection.items.${feature.key}.description`)}
							</p>
						</Card>
					))}
				</div>
			</section>

			{/* Workflow — a real sequence, so numbered steps carry meaning */}
			<section id="workflow" className="bg-content1/50 py-16 lg:py-24">
				<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
					<div className="mx-auto max-w-2xl text-center">
						<span className="text-sm font-semibold uppercase tracking-wider text-primary">
							{t("workflow.kicker")}
						</span>
						<h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
							{t("workflow.title")}
						</h2>
						<p className="mt-4 text-foreground-500">
							{t("workflow.description")}
						</p>
					</div>
					<div className="mt-12 grid gap-6 md:grid-cols-3">
						{WORKFLOW_STEPS.map((step) => (
							<div key={step} className="relative">
								<span className="font-display text-5xl font-bold text-primary/20">
									{t(`workflow.steps.${step}.step`)}
								</span>
								<h3 className="mt-2 text-xl font-semibold">
									{t(`workflow.steps.${step}.title`)}
								</h3>
								<p className="mt-2 text-foreground-500">
									{t(`workflow.steps.${step}.description`)}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Story */}
			<section
				id="stories"
				className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
			>
				<Card className="overflow-hidden border border-default-200/80">
					<div className="grid gap-8 p-8 md:grid-cols-[1.4fr_1fr] md:p-12">
						<div>
							<span className="text-sm font-semibold uppercase tracking-wider text-primary">
								{t("story.kicker")}
							</span>
							<h2 className="mt-2 font-display text-2xl font-bold sm:text-3xl">
								{t("story.title")}
							</h2>
							<blockquote className="mt-6 text-lg leading-relaxed text-foreground-600">
								„{t("story.quote")}"
							</blockquote>
							<div className="mt-6 flex items-center gap-3">
								<span className="flex size-11 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">
									{t("story.name").charAt(0)}
								</span>
								<div>
									<p className="font-semibold">{t("story.name")}</p>
									<p className="text-sm text-foreground-500">
										{t("story.role")}
									</p>
								</div>
							</div>
						</div>
						<div className="flex flex-col justify-center rounded-2xl bg-primary p-8 text-primary-foreground">
							<span className="text-sm font-medium opacity-80">
								{t("story.highlightKicker")}
							</span>
							<p className="mt-2 font-display text-5xl font-bold">
								{t("story.highlightValue")}
							</p>
							<p className="mt-2 opacity-90">{t("story.highlightLabel")}</p>
						</div>
					</div>
				</Card>
			</section>

			{/* Pricing */}
			<section id="pricing" className="bg-content1/50 py-16 lg:py-24">
				<div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
					<div className="mx-auto max-w-2xl text-center">
						<span className="text-sm font-semibold uppercase tracking-wider text-primary">
							{t("pricing.kicker")}
						</span>
						<h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">
							{t("pricing.title")}
						</h2>
						<p className="mt-4 text-foreground-500">
							{t("pricing.description")}
						</p>
					</div>
					<div className="mt-12 grid gap-6 md:grid-cols-2">
						{(["planOne", "planTwo"] as const).map((plan, index) => (
							<Card
								key={plan}
								className={`p-8 ${
									index === 1
										? "border-2 border-primary bg-primary/[0.03]"
										: "border border-default-200/80"
								}`}
							>
								<h3 className="text-lg font-semibold">
									{t(`pricing.${plan}.title`)}
								</h3>
								<p className="mt-2 font-display text-3xl font-bold">
									{t(`pricing.${plan}.price`)}
								</p>
								<p className="mt-1 text-sm text-foreground-500">
									{t(`pricing.${plan}.note`)}
								</p>
							</Card>
						))}
					</div>
					<div className="mx-auto mt-8 max-w-2xl">
						<p className="mb-4 text-center text-sm font-semibold uppercase tracking-wider text-foreground-500">
							{t("pricing.includesTitle")}
						</p>
						<div className="grid gap-3 sm:grid-cols-2">
							{(["one", "two", "three", "four"] as const).map((item) => (
								<div key={item} className="flex items-center gap-2">
									<Icon
										icon="lucide:check-circle-2"
										className="size-5 shrink-0 text-success"
									/>
									<span className="text-sm">
										{t(`pricing.includes.${item}`)}
									</span>
								</div>
							))}
						</div>
						<div className="mt-8 text-center">
							<Button asChild size="lg" variant="default">
								<Link to="/$locale/signup" params={{ locale }}>
									{t("pricing.cta")}
								</Link>
							</Button>
						</div>
					</div>
				</div>
			</section>

			{/* CTA */}
			<section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
				<Card className="relative overflow-hidden bg-primary p-10 text-center text-primary-foreground sm:p-16">
					<div className="pointer-events-none absolute -right-16 -top-16 size-64 rounded-full bg-white/10 blur-2xl" />
					<span className="text-sm font-medium opacity-80">
						{t("cta.kicker")}
					</span>
					<h2 className="mx-auto mt-2 max-w-2xl font-display text-3xl font-bold sm:text-4xl">
						{t("cta.title")}
					</h2>
					<p className="mx-auto mt-4 max-w-xl opacity-90">
						{t("cta.description")}
					</p>
					<Button
						asChild
						size="lg"
						className="bg-background font-semibold text-foreground"
						variant="muted"
					>
						<Link
							to="/$locale/signup"
							params={{ locale }}
							className="mt-8 inline-block"
						>
							{t("cta.button")}
							{<Icon icon="lucide:arrow-right" className="size-5" />}
						</Link>
					</Button>
				</Card>
			</section>

			{/* Footer */}
			<footer className="border-t border-default-100">
				<div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 py-8 sm:flex-row sm:justify-between sm:px-6 lg:px-8">
					<div className="flex items-center gap-2 font-display font-bold">
						<BrandLogo />
						{t("brand")}
					</div>
					<div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
						{NAV_ITEMS.map((item) => (
							<a
								key={item}
								href={`#${item}`}
								className="text-sm text-foreground-500 transition-colors hover:text-foreground"
							>
								{t(`footer.links.${item}`)}
							</a>
						))}
					</div>
					<p className="text-sm text-foreground-500">{t("footer.note")}</p>
				</div>
			</footer>
		</div>
	);
}
